import { createContext, useContext, useEffect, useState } from "react";

export const LANGUAGES = [
  { code: "en", label: "English", short: "EN" },
  { code: "it", label: "Italiano", short: "IT" },
];

export const translations = {
  en: {
    nav: { features: "Features", manifesto: "Manifesto", pricing: "Pricing", download: "Download", downloadNow: "Download now" },
    hero: {
      badge: "Now in private beta",
      headline: ["Communication that", "respects your cognitive", "bandwidth"],
      subtitle:
        "Rexx Calling filters the noise. Set your mental availability, let AI route only what's urgent, send with the right tone, drop messages at real places, and jump into crystal-clear video calls — all in one distraction-free workspace.",
      downloadNow: "Download now",
      comingSoon: "Coming soon on App Store & Google Play",
      scroll: "Scroll",
    },
    features: {
      label: "Six ways we filter the noise",
      title: "A workspace built around your attention, not against it.",
      items: {
        cognitive: { title: "Cognitive Availability States", desc: "Broadcast your real mental bandwidth. Let people know when you're in deep focus, open, or fully offline — before they even reach out." },
        routing: { title: "AI Routing & Silent Queue", desc: "Every incoming message is classified by urgency. Only what truly matters breaks through — the rest waits quietly in a silent queue." },
        empathy: { title: "Empathy Sync", desc: "Pre-send tone analysis detects brusqueness and sarcasm, so your words land the way you actually mean them." },
        video: { title: "Crystal-clear Video Calls", desc: "Jump into high-fidelity video calls with zero friction. Adaptive quality keeps every conversation sharp and lag-free." },
        place: { title: "Place Notes", desc: "Drop messages at real locations. Leave a note that only unlocks when someone arrives exactly where it matters." },
        notify: { title: "Smart Notifications", desc: "Notifications that respect your state. No pings during focus, no noise you didn't ask for — signal without the storm." },
      },
    },
    manifesto: {
      label: "The manifesto",
      chapters: [
        { title: "Attention is the scarcest resource you own.", body: "Every notification is a withdrawal from your focus. Rexx Calling treats your mental bandwidth as capital — spent deliberately, never drained by default." },
        { title: "Not every message deserves your now.", body: "AI reads urgency the way a good colleague would. The vital surfaces instantly; everything else rests in a silent queue until you choose to look." },
        { title: "Tone is the message. So we protect it.", body: "Empathy Sync catches the sharp edge before you send it. What you feel and what they read finally become the same thing." },
      ],
    },
    marquee: ["Respect Your Bandwidth", "Filter The Noise", "Send With Intention", "Focus Is Sacred"],
    pricing: {
      label: "Pricing",
      title: "Calm has a plan for everyone.",
      subtitle: "Start free. Upgrade when the noise gets loud. Cancel anytime.",
      mostPopular: "Most popular",
      secure: "Secure checkout powered by Stripe · Test card 4242 4242 4242 4242",
      perMonth: "per month",
      forever: "forever",
      plans: {
        free: { tagline: "The essentials, quietly.", cta: "Get started", features: ["Cognitive availability states", "Basic AI routing", "1-to-1 video calls", "Standard notifications"] },
        pro: { tagline: "Every feature, no compromise.", cta: "Go Pro", features: ["Everything in Free", "Empathy Sync tone analysis", "Silent queue & smart triage", "Place Notes", "HD group video calls", "Priority support"] },
        enterprise: { tagline: "Built for unlimited teams.", cta: "Scale up", features: ["Everything in Pro", "Unlimited team members", "Org-wide availability policies", "Advanced admin & analytics", "SSO & compliance controls", "Dedicated success manager"] },
      },
    },
    footer: { tagline: "Communication that respects your cognitive bandwidth. One distraction-free workspace.", network: "Rexxpons Network", networkDesc: "A network of legal, risk and communication tools by L.G.", visitNetwork: "Visit the network", apps: "Network apps", legal: "Legal & advisory", downloadNow: "Download now", copyright: "Rexxpons Network by L.G.", rights: "All rights reserved." },
    download: {
      label: "Download",
      title: "Get Rexx Calling on every device.",
      subtitle: "Download the app directly and start filtering the noise today. Mobile store versions are on the way.",
      directDownload: "Download",
      comingSoonStores: "Also coming soon on the Google Play Store and the Apple App Store.",
      platforms: {
        apple: { name: "Apple", desc: "For macOS & iOS devices." },
        android: { name: "Android", desc: "For Android phones & tablets." },
        windows: { name: "Windows", desc: "For Windows 10 & 11 desktops." },
      },
      badgeSoon: "Store version coming soon",
      back: "Back to home",
    },
    payment: {
      confirming: "Confirming your payment…", confirmingSub: "Hold tight, this only takes a moment.",
      paidTitle: "You're all set.", paidSub: "Welcome to Rexx Calling. Your subscription is active — the noise stops here.",
      timeoutTitle: "Still processing", timeoutSub: "Your payment is taking longer than usual. Check your email for confirmation.",
      errorTitle: "Something went wrong", errorSub: "We couldn't confirm this payment. No charge may have been made.",
      backHome: "Back to home",
      cancelTitle: "Checkout cancelled", cancelSub: "No charge was made. Whenever you're ready, your plan will be waiting.",
      viewPlans: "View plans again", home: "Home",
    },
  },

  it: {
    nav: { features: "Funzionalità", manifesto: "Manifesto", pricing: "Prezzi", download: "Download", downloadNow: "Scarica ora" },
    hero: {
      badge: "Ora in beta privata",
      headline: ["Comunicazione che", "rispetta la tua banda", "cognitiva"],
      subtitle:
        "Rexx Calling filtra il rumore. Imposta la tua disponibilità mentale, lascia che l'IA instradi solo ciò che è urgente, invia con il tono giusto, lascia messaggi in luoghi reali e avvia videochiamate cristalline — tutto in un unico spazio senza distrazioni.",
      downloadNow: "Scarica ora",
      comingSoon: "Presto su App Store e Google Play",
      scroll: "Scorri",
    },
    features: {
      label: "Sei modi per filtrare il rumore",
      title: "Uno spazio costruito attorno alla tua attenzione, non contro di essa.",
      items: {
        cognitive: { title: "Stati di Disponibilità Cognitiva", desc: "Comunica la tua reale banda mentale. Fai sapere quando sei in profonda concentrazione, disponibile o completamente offline — ancora prima che ti contattino." },
        routing: { title: "Instradamento IA e Coda Silenziosa", desc: "Ogni messaggio in arrivo è classificato per urgenza. Solo ciò che conta davvero emerge — il resto attende in silenzio in coda." },
        empathy: { title: "Empathy Sync", desc: "L'analisi del tono prima dell'invio rileva bruschezza e sarcasmo, così le tue parole arrivano come le intendi davvero." },
        video: { title: "Videochiamate Cristalline", desc: "Avvia videochiamate ad alta fedeltà senza frizioni. La qualità adattiva mantiene ogni conversazione nitida e senza ritardi." },
        place: { title: "Note nei Luoghi", desc: "Lascia messaggi in luoghi reali. Un messaggio che si sblocca solo quando qualcuno arriva esattamente dove conta." },
        notify: { title: "Notifiche Intelligenti", desc: "Notifiche che rispettano il tuo stato. Nessun ping durante la concentrazione, nessun rumore non richiesto — segnale senza tempesta." },
      },
    },
    manifesto: {
      label: "Il manifesto",
      chapters: [
        { title: "L'attenzione è la risorsa più scarsa che possiedi.", body: "Ogni notifica è un prelievo dalla tua concentrazione. Rexx Calling tratta la tua banda mentale come capitale — spesa con intenzione, mai prosciugata di default." },
        { title: "Non ogni messaggio merita il tuo adesso.", body: "L'IA legge l'urgenza come farebbe un buon collega. Ciò che è vitale emerge subito; tutto il resto riposa in una coda silenziosa finché non scegli di guardare." },
        { title: "Il tono è il messaggio. Per questo lo proteggiamo.", body: "Empathy Sync coglie lo spigolo tagliente prima che tu lo invii. Ciò che senti e ciò che leggono diventano finalmente la stessa cosa." },
      ],
    },
    marquee: ["Rispetta la tua banda", "Filtra il rumore", "Invia con intenzione", "La concentrazione è sacra"],
    pricing: {
      label: "Prezzi",
      title: "La calma ha un piano per tutti.",
      subtitle: "Inizia gratis. Passa a un piano superiore quando il rumore cresce. Disdici quando vuoi.",
      mostPopular: "Più popolare",
      secure: "Pagamento sicuro con Stripe · Carta di test 4242 4242 4242 4242",
      perMonth: "al mese",
      forever: "per sempre",
      plans: {
        free: { tagline: "L'essenziale, in silenzio.", cta: "Inizia ora", features: ["Stati di disponibilità cognitiva", "Instradamento IA di base", "Videochiamate 1 a 1", "Notifiche standard"] },
        pro: { tagline: "Tutte le funzioni, nessun compromesso.", cta: "Passa a Pro", features: ["Tutto di Free", "Analisi del tono Empathy Sync", "Coda silenziosa e triage intelligente", "Note nei Luoghi", "Videochiamate di gruppo HD", "Supporto prioritario"] },
        enterprise: { tagline: "Creato per team illimitati.", cta: "Cresci", features: ["Tutto di Pro", "Membri del team illimitati", "Politiche di disponibilità per l'organizzazione", "Amministrazione e analisi avanzate", "SSO e controlli di conformità", "Success manager dedicato"] },
      },
    },
    footer: { tagline: "Comunicazione che rispetta la tua banda cognitiva. Un unico spazio senza distrazioni.", network: "Rexxpons Network", networkDesc: "Un network di strumenti legali, di risk e di comunicazione firmato L.G.", visitNetwork: "Visita il network", apps: "App del network", legal: "Assistenza legale", downloadNow: "Scarica ora", copyright: "Rexxpons Network by L.G.", rights: "Tutti i diritti riservati." },
    download: {
      label: "Download",
      title: "Ottieni Rexx Calling su ogni dispositivo.",
      subtitle: "Scarica l'app direttamente e inizia a filtrare il rumore oggi. Le versioni per gli store mobili sono in arrivo.",
      directDownload: "Scarica",
      comingSoonStores: "Presto disponibile anche su Google Play Store e Apple App Store.",
      platforms: {
        apple: { name: "Apple", desc: "Per dispositivi macOS e iOS." },
        android: { name: "Android", desc: "Per telefoni e tablet Android." },
        windows: { name: "Windows", desc: "Per desktop Windows 10 e 11." },
      },
      badgeSoon: "Versione store in arrivo",
      back: "Torna alla home",
    },
    payment: {
      confirming: "Confermiamo il tuo pagamento…", confirmingSub: "Un attimo, ci vuole solo un momento.",
      paidTitle: "È tutto pronto.", paidSub: "Benvenuto in Rexx Calling. Il tuo abbonamento è attivo — il rumore finisce qui.",
      timeoutTitle: "Ancora in elaborazione", timeoutSub: "Il pagamento sta impiegando più del solito. Controlla la tua email per la conferma.",
      errorTitle: "Qualcosa è andato storto", errorSub: "Non siamo riusciti a confermare il pagamento. Potrebbe non esserci stato alcun addebito.",
      backHome: "Torna alla home",
      cancelTitle: "Pagamento annullato", cancelSub: "Nessun addebito effettuato. Quando vuoi, il tuo piano ti aspetta.",
      viewPlans: "Rivedi i piani", home: "Home",
    },
  },

  es: {
    nav: { features: "Funciones", manifesto: "Manifiesto", pricing: "Precios", download: "Descargar", downloadNow: "Descargar ahora" },
    hero: {
      badge: "Ahora en beta privada",
      headline: ["Comunicación que", "respeta tu ancho de banda", "cognitivo"],
      subtitle:
        "Rexx Calling filtra el ruido. Define tu disponibilidad mental, deja que la IA dirija solo lo urgente, envía con el tono adecuado, deja mensajes en lugares reales y entra en videollamadas nítidas — todo en un espacio sin distracciones.",
      downloadNow: "Descargar ahora",
      comingSoon: "Pronto en App Store y Google Play",
      scroll: "Desliza",
    },
    features: {
      label: "Seis maneras de filtrar el ruido",
      title: "Un espacio construido en torno a tu atención, no en su contra.",
      items: {
        cognitive: { title: "Estados de Disponibilidad Cognitiva", desc: "Comunica tu ancho de banda mental real. Haz saber cuándo estás en concentración profunda, disponible o totalmente desconectado — antes incluso de que te contacten." },
        routing: { title: "Enrutamiento IA y Cola Silenciosa", desc: "Cada mensaje entrante se clasifica por urgencia. Solo lo que de verdad importa se abre paso — el resto espera en silencio en una cola." },
        empathy: { title: "Empathy Sync", desc: "El análisis de tono antes del envío detecta brusquedad y sarcasmo, para que tus palabras lleguen como realmente las piensas." },
        video: { title: "Videollamadas Nítidas", desc: "Entra en videollamadas de alta fidelidad sin fricción. La calidad adaptativa mantiene cada conversación clara y sin retardos." },
        place: { title: "Notas de Lugar", desc: "Deja mensajes en ubicaciones reales. Una nota que solo se desbloquea cuando alguien llega exactamente donde importa." },
        notify: { title: "Notificaciones Inteligentes", desc: "Notificaciones que respetan tu estado. Sin avisos durante la concentración, sin ruido que no pediste — señal sin tormenta." },
      },
    },
    manifesto: {
      label: "El manifiesto",
      chapters: [
        { title: "La atención es el recurso más escaso que posees.", body: "Cada notificación es un retiro de tu concentración. Rexx Calling trata tu ancho de banda mental como capital — gastado con intención, nunca agotado por defecto." },
        { title: "No todo mensaje merece tu ahora.", body: "La IA lee la urgencia como lo haría un buen colega. Lo vital surge al instante; todo lo demás descansa en una cola silenciosa hasta que decides mirar." },
        { title: "El tono es el mensaje. Por eso lo protegemos.", body: "Empathy Sync detecta la arista afilada antes de que la envíes. Lo que sientes y lo que leen por fin se vuelven lo mismo." },
      ],
    },
    marquee: ["Respeta tu ancho de banda", "Filtra el ruido", "Envía con intención", "La concentración es sagrada"],
    pricing: {
      label: "Precios",
      title: "La calma tiene un plan para todos.",
      subtitle: "Empieza gratis. Mejora cuando el ruido aumente. Cancela cuando quieras.",
      mostPopular: "Más popular",
      secure: "Pago seguro con Stripe · Tarjeta de prueba 4242 4242 4242 4242",
      perMonth: "al mes",
      forever: "para siempre",
      plans: {
        free: { tagline: "Lo esencial, en silencio.", cta: "Empezar", features: ["Estados de disponibilidad cognitiva", "Enrutamiento IA básico", "Videollamadas 1 a 1", "Notificaciones estándar"] },
        pro: { tagline: "Todas las funciones, sin concesiones.", cta: "Pasar a Pro", features: ["Todo lo de Free", "Análisis de tono Empathy Sync", "Cola silenciosa y triaje inteligente", "Notas de Lugar", "Videollamadas grupales HD", "Soporte prioritario"] },
        enterprise: { tagline: "Creado para equipos ilimitados.", cta: "Escalar", features: ["Todo lo de Pro", "Miembros de equipo ilimitados", "Políticas de disponibilidad para toda la organización", "Administración y analíticas avanzadas", "SSO y controles de cumplimiento", "Gerente de éxito dedicado"] },
      },
    },
    footer: { tagline: "Comunicación que respeta tu ancho de banda cognitivo. Un único espacio sin distracciones.", network: "Rexxpons Network", downloadNow: "Descargar ahora", copyright: "Rexxpons Network by L.G. © 2026", rights: "Todos los derechos reservados." },
    download: {
      label: "Descargar",
      title: "Obtén Rexx Calling en todos tus dispositivos.",
      subtitle: "Descarga la app directamente y empieza a filtrar el ruido hoy. Las versiones para tiendas móviles están en camino.",
      directDownload: "Descargar",
      comingSoonStores: "Pronto también en Google Play Store y Apple App Store.",
      platforms: {
        apple: { name: "Apple", desc: "Para dispositivos macOS e iOS." },
        android: { name: "Android", desc: "Para teléfonos y tabletas Android." },
        windows: { name: "Windows", desc: "Para equipos Windows 10 y 11." },
      },
      badgeSoon: "Versión de tienda pronto",
      back: "Volver al inicio",
    },
    payment: {
      confirming: "Confirmando tu pago…", confirmingSub: "Un momento, esto solo tarda un instante.",
      paidTitle: "¡Todo listo!", paidSub: "Bienvenido a Rexx Calling. Tu suscripción está activa — el ruido termina aquí.",
      timeoutTitle: "Aún procesando", timeoutSub: "Tu pago está tardando más de lo habitual. Revisa tu correo para la confirmación.",
      errorTitle: "Algo salió mal", errorSub: "No pudimos confirmar este pago. Puede que no se haya realizado ningún cargo.",
      backHome: "Volver al inicio",
      cancelTitle: "Pago cancelado", cancelSub: "No se realizó ningún cargo. Cuando quieras, tu plan estará esperando.",
      viewPlans: "Ver planes de nuevo", home: "Inicio",
    },
  },

  fr: {
    nav: { features: "Fonctionnalités", manifesto: "Manifeste", pricing: "Tarifs", download: "Télécharger", downloadNow: "Télécharger" },
    hero: {
      badge: "Désormais en bêta privée",
      headline: ["Une communication qui", "respecte votre bande passante", "cognitive"],
      subtitle:
        "Rexx Calling filtre le bruit. Définissez votre disponibilité mentale, laissez l'IA n'acheminer que l'urgent, envoyez avec le bon ton, déposez des messages dans des lieux réels et lancez des appels vidéo limpides — le tout dans un espace sans distraction.",
      downloadNow: "Télécharger",
      comingSoon: "Bientôt sur l'App Store et Google Play",
      scroll: "Défiler",
    },
    features: {
      label: "Six façons de filtrer le bruit",
      title: "Un espace conçu autour de votre attention, non contre elle.",
      items: {
        cognitive: { title: "États de Disponibilité Cognitive", desc: "Diffusez votre véritable bande passante mentale. Faites savoir quand vous êtes en concentration profonde, disponible ou totalement hors ligne — avant même qu'on vous contacte." },
        routing: { title: "Acheminement IA et File Silencieuse", desc: "Chaque message entrant est classé par urgence. Seul l'essentiel perce — le reste attend en silence dans une file." },
        empathy: { title: "Empathy Sync", desc: "L'analyse du ton avant l'envoi détecte la brusquerie et le sarcasme, pour que vos mots arrivent comme vous le pensez vraiment." },
        video: { title: "Appels Vidéo Limpides", desc: "Lancez des appels vidéo haute fidélité sans friction. La qualité adaptative garde chaque conversation nette et sans latence." },
        place: { title: "Notes de Lieu", desc: "Déposez des messages dans des lieux réels. Une note qui ne se débloque que lorsque quelqu'un arrive exactement là où ça compte." },
        notify: { title: "Notifications Intelligentes", desc: "Des notifications qui respectent votre état. Aucune alerte pendant la concentration, aucun bruit non désiré — le signal sans la tempête." },
      },
    },
    manifesto: {
      label: "Le manifeste",
      chapters: [
        { title: "L'attention est la ressource la plus rare que vous possédez.", body: "Chaque notification est un retrait sur votre concentration. Rexx Calling traite votre bande passante mentale comme un capital — dépensé délibérément, jamais épuisé par défaut." },
        { title: "Tout message ne mérite pas votre instant présent.", body: "L'IA lit l'urgence comme le ferait un bon collègue. L'essentiel apparaît aussitôt ; tout le reste repose dans une file silencieuse jusqu'à ce que vous choisissiez de regarder." },
        { title: "Le ton est le message. Nous le protégeons donc.", body: "Empathy Sync capte l'angle tranchant avant que vous ne l'envoyiez. Ce que vous ressentez et ce qu'ils lisent deviennent enfin la même chose." },
      ],
    },
    marquee: ["Respectez votre bande passante", "Filtrez le bruit", "Envoyez avec intention", "La concentration est sacrée"],
    pricing: {
      label: "Tarifs",
      title: "Le calme a un plan pour chacun.",
      subtitle: "Commencez gratuitement. Passez à un plan supérieur quand le bruit monte. Annulez à tout moment.",
      mostPopular: "Le plus populaire",
      secure: "Paiement sécurisé par Stripe · Carte de test 4242 4242 4242 4242",
      perMonth: "par mois",
      forever: "pour toujours",
      plans: {
        free: { tagline: "L'essentiel, en silence.", cta: "Commencer", features: ["États de disponibilité cognitive", "Acheminement IA de base", "Appels vidéo en tête-à-tête", "Notifications standard"] },
        pro: { tagline: "Toutes les fonctions, sans compromis.", cta: "Passer à Pro", features: ["Tout de Free", "Analyse du ton Empathy Sync", "File silencieuse et tri intelligent", "Notes de Lieu", "Appels vidéo de groupe HD", "Support prioritaire"] },
        enterprise: { tagline: "Conçu pour des équipes illimitées.", cta: "Développer", features: ["Tout de Pro", "Membres d'équipe illimités", "Politiques de disponibilité pour toute l'organisation", "Administration et analyses avancées", "SSO et contrôles de conformité", "Responsable de la réussite dédié"] },
      },
    },
    footer: { tagline: "Une communication qui respecte votre bande passante cognitive. Un seul espace sans distraction.", network: "Rexxpons Network", downloadNow: "Télécharger", copyright: "Rexxpons Network by L.G. © 2026", rights: "Tous droits réservés." },
    download: {
      label: "Télécharger",
      title: "Obtenez Rexx Calling sur chaque appareil.",
      subtitle: "Téléchargez l'application directement et commencez à filtrer le bruit dès aujourd'hui. Les versions pour les stores mobiles arrivent bientôt.",
      directDownload: "Télécharger",
      comingSoonStores: "Bientôt aussi sur le Google Play Store et l'Apple App Store.",
      platforms: {
        apple: { name: "Apple", desc: "Pour les appareils macOS et iOS." },
        android: { name: "Android", desc: "Pour les téléphones et tablettes Android." },
        windows: { name: "Windows", desc: "Pour les ordinateurs Windows 10 et 11." },
      },
      badgeSoon: "Version store bientôt",
      back: "Retour à l'accueil",
    },
    payment: {
      confirming: "Confirmation de votre paiement…", confirmingSub: "Patientez, cela ne prend qu'un instant.",
      paidTitle: "Tout est prêt.", paidSub: "Bienvenue sur Rexx Calling. Votre abonnement est actif — le bruit s'arrête ici.",
      timeoutTitle: "Traitement en cours", timeoutSub: "Votre paiement prend plus de temps que d'habitude. Vérifiez votre e-mail pour la confirmation.",
      errorTitle: "Une erreur est survenue", errorSub: "Nous n'avons pas pu confirmer ce paiement. Aucun montant n'a peut-être été prélevé.",
      backHome: "Retour à l'accueil",
      cancelTitle: "Paiement annulé", cancelSub: "Aucun montant n'a été prélevé. Dès que vous serez prêt, votre plan vous attendra.",
      viewPlans: "Revoir les plans", home: "Accueil",
    },
  },
};

const LanguageContext = createContext(null);

function getByPath(obj, path) {
  return path.split(".").reduce((acc, key) => (acc == null ? undefined : acc[key]), obj);
}

export function LanguageProvider({ children }) {
  const [lang, setLang] = useState(() => {
    const saved = typeof window !== "undefined" ? localStorage.getItem("rexx_lang") : null;
    return saved && translations[saved] ? saved : "en";
  });

  useEffect(() => {
    localStorage.setItem("rexx_lang", lang);
    document.documentElement.lang = lang;
  }, [lang]);

  const t = (path) => {
    const val = getByPath(translations[lang], path);
    return val === undefined ? getByPath(translations.en, path) : val;
  };

  return <LanguageContext.Provider value={{ lang, setLang, t }}>{children}</LanguageContext.Provider>;
}

export function useLanguage() {
  const ctx = useContext(LanguageContext);
  if (!ctx) throw new Error("useLanguage must be used within LanguageProvider");
  return ctx;
}
